/**
 * Authentication Slice
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface Role {
  id: number;
  name: string;
  code: string;
}

interface EmployeeProfile {
  id: number;
  employee_id: string;
  role: Role | null;
  department: string | null;
  position: string | null;
  status: 'active' | 'inactive' | 'suspended';
  /** Present when loaded from some sources; profile API omits it. */
  is_active?: boolean;
  can_access_dashboard: boolean;
  /** When true, only custom_permissions (+ deps) apply; role does not grant access. */
  use_only_custom_permissions?: boolean;
  /** Effective permission codes (role ∪ custom by default, or custom-only). Use for access checks. */
  permissions: string[];
  /** Codes on the employee M2M; merged with role unless use_only_custom_permissions. */
  custom_permissions?: string[];
  /** Codes from the role only (audit); omitted from effective access when use_only_custom_permissions. */
  role_permissions?: string[];
  permission_breakdown?: {
    from_role: string[];
    extra_on_employee_record: string[];
    effective: string[];
    role_included_in_effective?: boolean;
  };
}

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  profile_picture: string | null;
  is_staff: boolean;
  is_superuser: boolean;
  employee_profile?: EmployeeProfile | null;
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
}

const initialState: AuthState = {
  user: JSON.parse(localStorage.getItem('user') || 'null'),
  token: localStorage.getItem('token'),
  refreshToken: localStorage.getItem('refreshToken'),
  isAuthenticated: !!localStorage.getItem('token'),
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (
      state,
      action: PayloadAction<{ user: User; token: string; refreshToken: string }>
    ) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
      state.isAuthenticated = true;

      localStorage.setItem('user', JSON.stringify(action.payload.user));
      localStorage.setItem('token', action.payload.token);
      localStorage.setItem('refreshToken', action.payload.refreshToken);
    },
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
      localStorage.setItem('token', action.payload);
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;

      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
        localStorage.setItem('user', JSON.stringify(state.user));
      }
    },
  },
});

export const { setCredentials, setToken, logout, updateUser } = authSlice.actions;
export default authSlice.reducer;

